import {
	CalendarDaysIcon as CalendarDaysIconOutline,
	PlayCircleIcon as PlayCircleIconOutline,
	TrophyIcon as TrophyIconOutline,
	UsersIcon as UsersIconOutline,
} from "@heroicons/react/24/outline";
import {
	CalendarDaysIcon,
	PlayCircleIcon,
	TrophyIcon,
	UsersIcon,
} from "@heroicons/react/24/solid";
import { Link, useMatchRoute, useRouteContext } from "@tanstack/react-router";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { graphql, useFragment } from "react-relay";
import { cn } from "@/utils";
import type { bottomTabs_club$key } from "./__generated__/bottomTabs_club.graphql";

export function BottomTabs({ club }: { club: bottomTabs_club$key }) {
	const { club: clubContext } = useRouteContext({ from: "__root__" });
	const data = useFragment<bottomTabs_club$key>(
		graphql`
    fragment bottomTabs_club on Club {
      id

   	  communityEnabled: configuration(key: "COMMUNITY_ENABLED")
			coursesEnabled: configuration(key: "COURSES_ENABLED")
			eventsEnabled: configuration(key: "EVENTS_ENABLED")
			rankingEnabled: configuration(key: "LEADERBOARD")

			events(orderBy: CHRONOLOGICAL) {
				edges {
					node {
						id
					}
				}
			}
			groups {
				spaces {
					isMember
					visibility
				}
			}
			coursePage {
				id
				categories(status: ACTIVE) {
					courses(status: ACTIVE) {
						visibility
						canView
					}
				}
			}
    }
  `,
		club,
	);

	const matchRoute = useMatchRoute();
	const { track } = useAnalytics();

	const hasEvents = data.events.edges.length;
	const hasCommunity = data.groups
		.filter(Boolean)
		.some((g) =>
			g.spaces
				.filter(Boolean)
				.some((s) => s.isMember || s.visibility === "PUBLIC"),
		);
	const hasCourses = data.coursePage.categories.some((c) =>
		c.courses.some(
			(c) =>
				c.visibility === "PRIVATE" ||
				c.visibility === "PUBLIC" ||
				c.visibility === "MEMBER_ONLY" ||
				c.canView,
		),
	);

	const isCoursesEnabled =
		typeof data.coursesEnabled !== "string" || data.coursesEnabled === "1";
	const isCommunityEnabled =
		typeof data.communityEnabled !== "string" || data.communityEnabled === "1";
	const isEventsEnabled =
		typeof data.eventsEnabled !== "string" || data.eventsEnabled === "1";
	const isLeaderboardEnabled =
		typeof data.rankingEnabled !== "string" ||
		data.rankingEnabled === "1" ||
		data.rankingEnabled === "ENABLED";

	const isCourses =
		Boolean(
			matchRoute({ to: "/cursos" }) ||
				matchRoute({ to: "/cursos/$course" }) ||
				matchRoute({ to: "/cursos/$course/$lesson" }),
		) || false;

	const isCommunity =
		Boolean(
			matchRoute({ to: "/comunidade" }) ||
				matchRoute({ to: "/comunidade/s/$space" }) ||
				matchRoute({ to: "/comunidade/s/$space/posts/$post" }) ||
				matchRoute({ to: "/posts/novo" }),
		) || false;

	const isEvents =
		Boolean(
			matchRoute({ to: "/eventos" }) ||
				matchRoute({ to: "/eventos/$slug" }) ||
				matchRoute({ to: "/eventos/novo" }) ||
				matchRoute({ to: "/eventos/$slug/editar" }),
		) || false;

	const isRanking =
		Boolean(
			matchRoute({ to: "/ranking/leaderboard" }) ||
				matchRoute({ to: "/ranking/pontuacao" }),
		) || false;

	const isAdmin =
		clubContext.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;
	const isModerator =
		clubContext.permissions?.some((p) => p.slug === "MODERATOR") || false;

	const tabCount = [
		isAdmin || Boolean(hasCourses && isCoursesEnabled),
		isAdmin || isModerator || Boolean(hasCommunity && isCommunityEnabled),
		isAdmin || Boolean(hasEvents && isEventsEnabled),
		isLeaderboardEnabled,
	].filter((x) => !!x).length;

	if (tabCount <= 1) {
		return null;
	}

	const grid: Record<string, `grid-cols-${number}`> = {
		"2": "grid-cols-2",
		"3": "grid-cols-3",
		"4": "grid-cols-4",
	} as const;

	return (
		<>
			<div className="h-20 md:hidden mt-6" />
			{/* biome-ignore lint/correctness/useUniqueElementIds: must be static */}
			<div
				id="bottom-tabs"
				className={cn(
					"h-20 bg-background md:hidden border-t border-border text-foreground fixed z-2 bottom-0 w-screen",
				)}
			>
				<ul
					className={cn(
						"grid size-full",
						grid[tabCount.toString()] || "grid-cols-1",
					)}
				>
					{isAdmin || (hasCourses && isCoursesEnabled) ? (
						<li>
							<Link
								to="/cursos"
								onClick={() =>
									track(AnalyticsEvents.NAV_TAB_CLICKED, {
										tab: "cursos",
										source: "mobile",
									})
								}
								className={cn(
									"size-full flex flex-col items-center justify-center gap-1 text-sm",
									isCourses &&
										"border-t-2 box-border font-semibold border-primary! text-primary",
								)}
							>
								{isCourses ? (
									<PlayCircleIcon className="size-5" />
								) : (
									<PlayCircleIconOutline className="size-5" />
								)}
								Cursos
							</Link>
						</li>
					) : null}

					{isAdmin || isModerator || (hasCommunity && isCommunityEnabled) ? (
						<li>
							<Link
								to="/comunidade"
								onClick={() =>
									track(AnalyticsEvents.NAV_TAB_CLICKED, {
										tab: "comunidade",
										source: "mobile",
									})
								}
								className={cn(
									"size-full flex flex-col items-center justify-center gap-1 text-sm",
									isCommunity &&
										"border-t-2 font-semibold border-primary! text-primary",
								)}
							>
								{isCommunity ? (
									<UsersIcon className="size-5" />
								) : (
									<UsersIconOutline className="size-5" />
								)}
								Comunidade
							</Link>
						</li>
					) : null}

					{isAdmin || (hasEvents && isEventsEnabled) ? (
						<li>
							<Link
								to="/eventos"
								onClick={() =>
									track(AnalyticsEvents.NAV_TAB_CLICKED, {
										tab: "eventos",
										source: "mobile",
									})
								}
								className={cn(
									"size-full flex flex-col items-center justify-center gap-1 text-sm",
									isEvents &&
										"border-t-2 font-semibold border-primary! text-primary",
								)}
							>
								{isEvents ? (
									<CalendarDaysIcon className="size-5" />
								) : (
									<CalendarDaysIconOutline className="size-5" />
								)}
								Eventos
							</Link>
						</li>
					) : null}

					{isLeaderboardEnabled ? (
						<li>
							<Link
								to="/ranking/leaderboard"
								onClick={() =>
									track(AnalyticsEvents.NAV_TAB_CLICKED, {
										tab: "ranking",
										source: "mobile",
									})
								}
								className={cn(
									"flex size-full flex-col items-center justify-center gap-1 text-sm",
									isRanking &&
										"border-t-2 font-semibold border-primary! text-primary",
								)}
							>
								{isRanking ? (
									<TrophyIcon className="size-5" />
								) : (
									<TrophyIconOutline className="size-5" />
								)}
								Ranking
							</Link>
						</li>
					) : null}
				</ul>
			</div>
		</>
	);
}
